import { Warning } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { route } from "../urls";
import { switchAccount } from "./account-picker";
import { Avatar } from "./avatar";
interface ValidAccountSelectOption {
  avatar_url: string | null;
  email: string | null;
  kind: "user" | "organization";
  username: string;
}

export type AccountSelectOption = ValidAccountSelectOption & {
  avatar_url: string | null;
  kind: "user" | "organization" | null;
  name: string | null;
};

export default function AccountSelect({
  accounts,
  id,
  name,
  value,
}: {
  accounts: AccountSelectOption[];
  id: string;
  name: string;
  value: string;
}) {
  const validAccounts = accounts.filter(
    (account) => account.username && account.kind
  ) as ValidAccountSelectOption[];

  if (validAccounts.length === 0) {
    return (
      <div className="tw-select">
        <select
          className="w-full border p-2 font-semibold border-r8-gray-12 bg-white dark:bg-r8-gray-1 disabled:cursor-not-allowed disabled:opacity-50"
          name={name}
          id={id}
          disabled
          value=""
        >
          <option value="">No valid accounts found</option>
        </select>
      </div>
    );
  }

  const initialAccount =
    validAccounts.find((account) => account.username === value) ??
    validAccounts[0];
  const [currentAccount, setCurrentAccount] = useState(initialAccount);

  const mutation = useMutation({
    mutationFn: switchAccount,
    onSuccess: () => {
      window.location.href = route("account_email_settings");
    },
    onError: () => {
      toast.error("Failed to switch account. Please try again.");
    },
  });

  return (
    <div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 flex items-center justify-center pl-2 pointer-events-none">
          <Avatar
            className="w-8 h-8"
            avatar={{
              kind: currentAccount.kind,
              url: currentAccount.avatar_url,
            }}
          />
        </div>
        <div className="tw-select">
          <select
            value={currentAccount?.username ?? ""}
            onChange={(e) => {
              const account = validAccounts.find(
                (account) => account.username === e.target.value
              );
              if (account) {
                setCurrentAccount(account);
              }
            }}
            className="w-full border p-2 !pl-12 font-semibold border-r8-gray-12 bg-white dark:bg-r8-gray-1 disabled:cursor-not-allowed disabled:opacity-50"
            name={name}
            id={id}
          >
            {validAccounts.map((account) => (
              <option key={account.username} value={account.username}>
                {account.username}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-1 text-r8-sm text-r8-gray-11">
        {currentAccount?.email ? (
          <p>
            <span className="font-semibold">Email address:</span>{" "}
            {currentAccount.email}
          </p>
        ) : (
          <div className="flex gap-1">
            <Warning className="inline-block align-text-bottom fill-r8-red-11 flex-shrink-0 mt-[3px]" />
            <p>
              <span className="text-r8-red-11 font-semibold">
                Email address not found.
              </span>{" "}
              To submit a support case,{" "}
              <button
                type="button"
                className="font-semibold underline hover:no-underline"
                onClick={() => {
                  if (
                    !confirm(
                      "This action will open a new page. Any unsaved changes will be lost. Continue?"
                    )
                  ) {
                    return;
                  }
                  const account = accounts.find(
                    (acct) => acct.username === currentAccount.username
                  );
                  if (account) {
                    mutation.mutate({ account: account });
                  }
                }}
              >
                add an email address
              </button>
              .
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
